/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import { useAuth } from "../../providers";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { api } from "@app/data/index";
import { LemmonModal } from "@lemmonhq/uikit";
import { User } from "@lemmonhq/types";
import { Loading } from "../../components";
import { NumPad } from "@lemmonhq/uikit";
export type ColleagueProfile = { id: number; name: string };

export const AppLanding = defineComponent({
  name: "AppLanding",
  setup() {
    const auth = useAuth();
    const router = useRouter();

    // New state for PIN login feature
    const staffMembers = ref<ColleagueProfile[]>([]);
    const pinModalOpen = ref(false);
    const selectedStaff = ref<null | ColleagueProfile>(null);
    const pin = ref("");
    const pinError = ref("");
    const loaded = ref(false);
    const loggingActive = ref(false);
    const route = useRoute();

    const redirect = (user: User) => {
      const qs = new URLSearchParams(
        (route.query as Record<string, string>) || {},
      ).toString();
      if (user.role === "staff") {
        return router.replace(`/app/pos?${qs}`);
      } else if (user.role === "manager") {
        return router.replace(`/app/reports?${qs}`);
      } else if (user.role === "admin") {
        return router.replace(`/app/admin?${qs}`);
      }
    };

    onMounted(async () => {
      if (!auth.user.value) {
        const shopId = localStorage.getItem("shopId");
        if (shopId) {
          try {
            const members = await api.getPublicStaffList(shopId);
            staffMembers.value = members.filter(
              (el) => el.status === "active" && !el.deleted_at,
            );
          } catch (err) {
            console.error("Failed to fetch staff members", err);
          } finally {
            loaded.value = true;
          }
        } else {
          loaded.value = true;
        }
      } else {
        return redirect(auth.user.value);
      }
    });

    // Handlers for staff PIN login
    const handleStaffClick = (staff: ColleagueProfile) => {
      selectedStaff.value = staff;
      pin.value = "";
      pinError.value = "";
      pinModalOpen.value = true;
    };

    const handlePinSubmit = async () => {
      if (!selectedStaff.value) return;
      const shopId = Number(localStorage.getItem("shopId"));
      if (!Number.isSafeInteger(shopId) || shopId <= 0) {
        pinError.value =
          "Unable to determine this shop. Please log in with your password.";
        return;
      }
      if (!pin.value) {
        pinError.value = "Please enter PIN";
        return;
      }
      loggingActive.value = true;
      try {
        const res = await api.loginWithPin(
          shopId,
          selectedStaff.value.id,
          pin.value,
        );
        if (res) {
          auth.setUser(res);
          redirect(res);
        } else {
          pinError.value = "Invalid PIN. Please try again.";
        }
      } catch (e) {
        pinError.value = "Invalid PIN. Please try again.";
      } finally {
        loggingActive.value = false;
      }
    };

    return () => {
      const shopId = localStorage.getItem("shopId");
      return (
        <div class="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto">
          <div class="flex flex-col md:flex-row w-full max-w-[800px] justify-center items-center">
            <div class="flex flex-col flex-1 justify-center items-center px-8 py-4 max-w-[400px]">
              <RouterLink
                class="btn btn-primary btn-lg w-full my-4"
                to="/login/email"
              >
                <span class="material-symbols-outlined mr-0 text-3xl">
                  fingerprint
                </span>{" "}
                Login with password
              </RouterLink>
            </div>
            <div
              style={shopId ? "" : "display: none;"}
              class={
                "flex flex-1 justify-center items-center p-4 md:border-l  "
              }
            >
              {loaded.value ? (
                <>
                  {staffMembers.value.length > 0 && (
                    <div class="mt-6">
                      <h3 class=" text-center font-semibold text-accent mb-4 font-headline text-3xl">
                        PIN Login
                      </h3>
                      <div class="grid grid-cols-3 gap-4">
                        {staffMembers.value.map((staff) => {
                          const [firstName, lastName] = staff.name.split(" ");
                          return (
                            <div
                              key={staff.id}
                              class="flex flex-col items-center cursor-pointer"
                              onClick={() => handleStaffClick(staff)}
                            >
                              <div class="avatar">
                                <div class="w-16 rounded-full">
                                  <div class="flex items-center justify-center bg-accent text-accent-content w-full h-full">
                                    {`${firstName ? firstName.charAt(0) : ""}${
                                      lastName ? lastName.charAt(0) : ""
                                    }`}
                                  </div>
                                </div>
                              </div>
                              <span class="mt-2  text-center">
                                {staff.name}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div class="w-full flex justify-center items-center py-6">
                  <Loading />
                </div>
              )}
            </div>
          </div>

          {pinModalOpen.value && selectedStaff.value && (
            <LemmonModal
              onClose={() => {
                pinModalOpen.value = false;
                selectedStaff.value = null;
              }}
              title="PIN Login"
              style="max-width: 400px;"
            >
              <div class="p-4">
                <NumPad
                  value={pin.value}
                  onChange={(nv) => {
                    pin.value = nv;
                    pinError.value = "";
                  }}
                  secureDisplay={true}
                />
                {pinError.value && (
                  <div role="alert" class="alert alert-error mt-4">
                    <span>{pinError.value}</span>
                  </div>
                )}
                <div class="mt-6">
                  <button
                    class="btn btn-primary font-normal btn-lg w-full text-3xl"
                    disabled={!!loggingActive.value || !pin.value}
                    onClick={handlePinSubmit}
                  >
                    <span class="material-symbols-outlined mr-0 text-3xl">
                      fingerprint
                    </span>{" "}
                    Login
                  </button>
                </div>
              </div>
            </LemmonModal>
          )}
        </div>
      );
    };
  },
});
